import { useState } from "react";
import { Play, AlertCircle, ShoppingCart, ImageIcon, Clock, Check } from "lucide-react";
import { cn, isDirectVideoUrl, normalizeVideoUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import noVideoPlaceholder from "@/assets/no-video-placeholder.png";
import { format, parseISO } from "date-fns";
import type { ShipDateColorSlot } from "@/lib/shipDateColors";
import { normalizeGradeLabel } from "@shared/grading";

const DEFAULT_SLOT: ShipDateColorSlot = {
  bannerBg: 'bg-amber-500 dark:bg-amber-600',
  cardBorder: 'border-amber-400 dark:border-amber-600',
  badgeBg: '#f59e0b',
  text: 'text-white',
};

interface VideoThumbnailCardProps {
  serialNumber: string;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  grade: string;
  notes?: string | null;
  noVideoExpected?: boolean;
  shipDate?: string | null;
  onClick?: () => void;
  onAddToCart?: () => void;
  isInCart?: boolean;
  className?: string;
  colorSlot?: ShipDateColorSlot;
  /**
   * Compact preview mode used inside collapsed accordions:
   *   - smaller card chrome
   *   - no Add-to-cart button, no notes, no play hover overlay
   * The same component is reused so we don't duplicate JSX.
   */
  compact?: boolean;
}

export function VideoThumbnailCard({
  serialNumber,
  videoUrl,
  thumbnailUrl: storedThumbnailUrl,
  grade,
  notes,
  noVideoExpected = false,
  shipDate,
  onClick,
  onAddToCart,
  isInCart = false,
  className,
  colorSlot = DEFAULT_SLOT,
  compact = false,
}: VideoThumbnailCardProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Normalize storage paths / Dropbox share links into direct-file URLs first
  const normalizedVideoUrl = normalizeVideoUrl(videoUrl);
  // Only treat a URL as playable if it is a direct video file.
  // Non-playable URLs (e.g. supplier inspection pages) are treated as no video
  // and are never linked or surfaced to customers.
  const hasDirectVideo = !!normalizedVideoUrl && isDirectVideoUrl(normalizedVideoUrl);
  const isNoVideo = !hasDirectVideo;

  const thumbnailUrl = hasDirectVideo
    ? storedThumbnailUrl || `/api/video-thumbnail?path=${encodeURIComponent(normalizedVideoUrl || '')}&v=2`
    : noVideoPlaceholder;

  const formattedShipDate = shipDate
    ? format(typeof shipDate === 'string' ? parseISO(shipDate) : new Date(shipDate), 'MMM d, yyyy')
    : null;

  const gradeColors: Record<string, { bg: string; text: string; border: string }> = {
    'A': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
    'A-': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
    'B+': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
    'B': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
    'B-': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
    'C': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  };

  const displayGrade = normalizeGradeLabel(grade);
  const colors = gradeColors[displayGrade] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (hasDirectVideo && onClick) onClick();
    }
  };

  const isInteractive = hasDirectVideo && !!onClick && !compact;

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={handleKeyDown}
      className={cn(
        "group relative overflow-hidden rounded-lg border bg-muted transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        isInteractive && "hover:shadow-lg hover:scale-[1.02] cursor-pointer",
        shipDate && colorSlot.cardBorder,
        className
      )}
      data-testid={`video-thumbnail-${serialNumber}${compact ? '-compact' : ''}`}
    >
      <div className="aspect-video relative">
        {!imageError ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted animate-pulse">
                <div className={cn("rounded-full bg-muted-foreground/20", compact ? "w-6 h-6" : "w-12 h-12")} />
              </div>
            )}
            <img
              src={thumbnailUrl}
              alt={`Video for ${serialNumber}`}
              className={cn(
                "w-full h-full object-cover transition-opacity duration-300",
                imageLoaded ? "opacity-100" : "opacity-0"
              )}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-gray-400">
            <AlertCircle className={cn(compact ? "w-4 h-4" : "w-8 h-8 mb-2")} />
            {!compact && <span className="text-xs">Video Preview</span>}
          </div>
        )}

        {/* Play button — only shown for direct video URLs on hover, never in compact mode */}
        {hasDirectVideo && !compact && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
              <Play className="w-7 h-7 text-gray-900 ml-1" fill="currentColor" />
            </div>
          </div>
        )}

        {!compact && (
          <div className="absolute top-2 right-2">
            <span className={cn(
              "inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border",
              colors.bg,
              colors.text,
              colors.border
            )}>
              {displayGrade}
            </span>
          </div>
        )}

        {formattedShipDate && !compact && (
          <div className="absolute bottom-2 left-2 right-2">
            <div className={cn("text-center py-1 px-2 rounded-md shadow-sm", colorSlot.bannerBg)}>
              <div className={cn("flex items-center justify-center gap-1 text-xs font-medium", colorSlot.text)}>
                <Clock className="w-3 h-3 shrink-0" />
                <span>Ships {formattedShipDate}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {compact ? (
        <div className="px-1.5 py-1 bg-background">
          <span
            className="block font-mono text-[10px] font-medium text-foreground break-all leading-tight"
            title={serialNumber}
          >
            {serialNumber}
          </span>
        </div>
      ) : (
        <div className="p-2 bg-background">
          <span className="block font-mono text-xs font-medium text-foreground mb-1.5 break-all leading-snug">
            {serialNumber}
          </span>
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
              <ImageIcon className="w-3 h-3" />
              <span>{hasDirectVideo ? "Video" : "No Video"}</span>
            </div>
            {isInCart ? (
              <Button
                size="sm"
                disabled
                className="bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 h-7 px-2 gap-1 shrink-0 cursor-default"
                data-testid={`button-in-cart-${serialNumber}`}
              >
                <Check className="w-3 h-3" />
                <span className="text-xs">In Cart</span>
              </Button>
            ) : onAddToCart ? (
              <Button
                size="sm"
                className="bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 h-7 px-2 gap-1 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToCart();
                }}
                data-testid={`button-add-to-cart-${serialNumber}`}
              >
                <ShoppingCart className="w-3 h-3" />
                <span className="text-xs">Add</span>
              </Button>
            ) : null}
          </div>
          {notes && (
            <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
              {notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
